import { db, fbAuth, storage } from "../../connection";
import { applicationError } from "../../../../redux/actions/error";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "@firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "@firebase/storage";
import axios from "axios";
import logger from "../../../../utils/logger";
import { postUpdateCDNUtil } from "../../../../helperFunctions/util";

export function getMyId() {
  return fbAuth.currentUser.uid;
}

export function updateUserRole(groupId, memberId, role, currentRole) {
  let promises = [];
  promises.push(
    new Promise((resolve, reject) => {
      setDoc(
        doc(db, "userGroup", groupId, "members", memberId),
        { userRole: role },
        { merge: true },
      )
        .then(() => {
          resolve();
        })
        .catch((e) => {
          reject(e);
        });
    }),
  );

  if (role === "fan" || currentRole === "fan") {
    let url = `${import.meta.env.VITE_GCP_BASE_URL}/subscribeUersToTopics?groupId=${groupId}&&userId=${memberId}&&userRole=${role}`;

    promises.push(fetch(url));
  }

  return Promise.all(promises);
}

export function fbGetMembers(groupId) {
  return new Promise((resolve, reject) => {
    getDocs(collection(db, "userGroup", groupId, "members"))
      .then((querySnapshot) => {
        const members = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};

          return {
            id: docSnap.id,
            userRole: data.userRole || "",
            registeredOnChantDate: data.registrationDate || data.addedOn,
            ...data,
          };
        });

        resolve(members);
      })
      .catch((err) => {
        let message = "There was an error fetching members for the group";
        applicationError({
          message,
          intensity: "low",
          show: true,
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
}

export function fbGetLatestMembers(groupId, date) {
  return new Promise((resolve, reject) => {
    getDocs(
      query(
        collection(db, "userGroup", groupId, "members"),
        where("registrationDate", ">", date),
      ),
    )
      .then((querySnapshot) => {
        let result = [];
        querySnapshot.docs.forEach((doc) => {
          result.push({ ...doc.data(), id: doc.id });
        });
        resolve(result);
      })
      .catch((err) => {
        let message = "There was an error fetching members for the group";
        applicationError({
          message,
          intensity: "low",
          show: true,
          err,
          errorCode: "firebase",
        });
        reject({ message });
      });
  });
}

export function fbLockMember(groupId, memberId) {
  return new Promise((resolve, reject) => {
    setDoc(
      doc(db, "userGroup", groupId, "members", memberId),
      { lockStatus: true },
      { merge: true },
    )
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

export function fbUnlockMember(groupId, memberId) {
  return new Promise((resolve, reject) => {
    setDoc(
      doc(db, "userGroup", groupId, "members", memberId),
      { lockStatus: false },
      { merge: true },
    )
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
}

export function fbGetPendingMembers(groupId) {
  return getDoc(doc(db, "invite_emails", groupId));
}

export function fbSaveFormLayout(layoutData, groupId) {
  return new Promise((resolve, reject) => {
    let docRef = doc(db, "registration_form_layout", groupId);
    setDoc(docRef, layoutData, {
      merge: true,
    })
      .then(() => {
        postUpdateCDNUtil(groupId, "join");
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function fbGetFormLayout(groupId) {
  return new Promise((resolve, reject) => {
    let docRef = doc(db, "registration_form_layout", groupId);
    getDoc(docRef)
      .then(async (doc) => {
        if (doc.exists()) {
          let packages = await getPackages(groupId);
          resolve({ ...(doc.data() || {}), id: doc.id, packages });
        }
        resolve();
      })
      .catch(reject);
  });
}

export function publishOrUnpublishForm(isPublished, groupId, fullGroupName) {
  return new Promise((resolve) => {
    let docRef = doc(db, "registration_form_layout", groupId);
    setDoc(
      docRef,
      {
        isPublished,
        groupName: fullGroupName,
      },
      {
        merge: true,
      },
    ).then(() => {
      getDoc(docRef).then((doc) => {
        let data = doc.data() || {};
        resolve(data.isPublished);
      });
    });
  });
}

export function uploadImage(file, path, index) {
  return new Promise((resolve, reject) => {
    let storageRef = ref(storage);
    let folderRef = ref(storageRef, `${path}`);
    uploadBytes(folderRef, file)
      .then((snapshot) => {
        return getDownloadURL(snapshot.ref);
      })
      .then((downloadUrl) => {
        resolve({ downloadUrl, index });
      })
      .catch(reject);
  });
}

export function getPackages(groupId) {
  return new Promise((resolve) => {
    getDocs(
      collection(db, "registration_form_layout", groupId, "packages"),
    ).then((snapshot) => {
      let data = [];
      snapshot.docs.forEach((doc) => {
        if (doc.exists() && !(doc.data() || {}).isDeleted) {
          data.push({
            ...(doc.data() || {}),
            id: doc.id,
            sortIndex: (doc.data() || {}).sortOrder || 9999,
          });
        }
      });

      data = data.sort((a, b) => a.sortIndex - b.sortIndex);
      resolve(data);
    });
  });
}

export function getAllPackages(groupId) {
  return new Promise((resolve) => {
    getDocs(
      collection(db, "registration_form_layout", groupId, "packages"),
    ).then((snapshot) => {
      let data = [];
      snapshot.docs.forEach((doc) => {
        if (doc.exists()) {
          data.push({ ...doc.data(), id: doc.id });
        }
      });
      resolve(data);
    });
  });
}

export function addPackage(data, groupId, merchantId, currency) {
  let expiryDate = data.packageExpiryDate;

  data.sortOrder = 1;

  return new Promise((resolve, reject) => {
    let docRef = doc(
      collection(db, "registration_form_layout", groupId, "packages"),
    );
    let promises = [];

    let packageUpdatePromise = setDoc(docRef, data, {
      merge: true,
    }).catch((err) => {
      reject(err);
    });

    let expiryDateUpdatePromise = getDoc(doc(db, "group", groupId))
      .then((docSnap) => {
        if (docSnap.exists()) {
          let packageIdExpiryMap = docSnap.data().packageIdExpiryMap || {};
          let packageExpiryMap = docSnap.data().packageExpiryMap || {};
          packageIdExpiryMap[docRef.id] = expiryDate;
          packageExpiryMap[docRef.id] = {
            expiryDate: expiryDate || 0,
            name: data.name,
          };
          updateDoc(doc(db, "group", groupId), {
            packageIdExpiryMap,
            packageExpiryMap,
          });
        }
      })
      .catch(reject);
    promises = [packageUpdatePromise, expiryDateUpdatePromise];
    if (data.offerAutorenewal) {
      let reqBody = {
        name: data.name,
        packageId: docRef.id,
        groupId: groupId,
        price: data.price,
        merchantId: merchantId,
        currency: currency,
      };
      let url = `${import.meta.env.VITE_GCP_BASE_URL}/stripe2/createProduct`;
      promises.push(
        axios
          .post(url, JSON.stringify(reqBody), {
            headers: {
              "content-type": "text/json",
            },
          })
          .then((res) => {
            return {
              productId: res.data.productId,
              priceId: res.data.priceId,
              shippingPriceId: res.data.shippingPriceId,
              shippingPriceIdWithFee: res.data.shippingPriceIdWithFee,
              priceIdWithFee: res.data.priceIdWithFee,
              secondaryPriceIdWithFee: res.data.secondaryPriceIdWithFees,
              secondaryShippingPriceIdWithFee:
                res.data.secondaryShippingPriceIdWithFees,
            };
          })
          .catch((err) => {
            logger.error("Error ", err);
          }),
      );

      if (data.childPrice) {
        let childReqBody = {
          name: `${data.name}-children`,
          packageId: docRef.id,
          groupId: groupId,
          price: data.childPrice,
          merchantId: merchantId,
          currency: currency,
          isSubProduct: true,
        };
        promises.push(
          axios
            .post(url, JSON.stringify(childReqBody), {
              headers: {
                "content-type": "text/json",
              },
            })
            .then((res) => {
              return {
                productId: res.data.productId,
                priceId: res.data.priceId,
                shippingPriceId: res.data.shippingPriceId,
                shippingPriceIdWithFee: res.data.shippingPriceIdWithFee,
                priceIdWithFee: res.data.priceIdWithFee,
              };
            })
            .catch((err) => {
              logger.error("Error ", err);
            }),
        );
      }
    }

    Promise.all(promises)
      .then(async (res) => {
        let batch = writeBatch(db);

        let snap = await getDocs(
          collection(db, "registration_form_layout", groupId, "packages"),
        );
        snap.docs.forEach((doc) => {
          if (doc.id !== docRef.id && doc.data().sortOrder) {
            batch.update(doc.ref, { sortOrder: increment(1) });
          }
        });

        await batch.commit();

        if (res.length >= 3) {
          let product = res[2];
          let updateObj = {
            stripeProductId: product.productId,
            price: {
              ...data.price,
              priceId: product.priceId,
              shippingPriceId: product.shippingPriceId,
              priceIdWithFee: product.priceIdWithFee,
              shippingPriceIdWithFee: product.shippingPriceIdWithFee,
              secondaryPriceIdWithFee: product.secondaryPriceIdWithFee,
              secondaryShippingPriceIdWithFee:
                product.secondaryShippingPriceIdWithFee,
            },
          };
          if (res.length >= 4) {
            let childProduct = res[3];
            updateObj = {
              ...updateObj,
              childPrice: {
                ...data.childPrice,
                priceId: childProduct.priceId,
                shippingPriceId: childProduct.shippingPriceId,
                priceIdWithFee: childProduct.priceIdWithFee,
                shippingPriceIdWithFee: childProduct.shippingPriceIdWithFee,
                childProductId: childProduct.productId,
              },
            };
          }
          updateDoc(docRef, updateObj);
        }
      })
      .then(() => {
        resolve();
      });
  });
}

// Posts a price block to stripe2/updateProduct and returns its response body.
async function postProductUpdate(url, reqBody) {
  const res = await axios.post(url, JSON.stringify(reqBody), {
    headers: {
      "content-type": "text/json",
    },
  });
  logger.log(JSON.parse(JSON.stringify(res)));
  return res.data;
}

export async function updatePackage(
  data,
  packageId,
  groupId,
  editChanges,
  merchantId,
  currency,
) {
  let expiryDate = data.packageExpiryDate;
  let docRef = doc(
    db,
    "registration_form_layout",
    groupId,
    "packages",
    packageId,
  );

  if (
    !Object.keys(data.price).includes("priceId") ||
    !Object.keys(data.price).includes("secondaryPriceIdWithFee")
  ) {
    editChanges.priceChange = true;
    editChanges.productId = data.stripeProductId;
  }

  if (Object.keys(data.childPrice).includes("childProductId")) {
    editChanges.childProductId = data.childPrice.childProductId;
  }

  let price = data.price;
  let childPrice = data.childPrice;
  let stripeProductId = data.stripeProductId;

  // Stripe prices are created first and the package is written once, afterwards,
  // with every id they return. Writing the form before Stripe answered left a
  // package with its new amount but the previous prices whenever the call failed,
  // and saving only priceId / shippingPriceId left the *WithFee ids — which
  // checkout charges whenever the member covers fees — on the previous prices.
  if (
    editChanges.nameChange ||
    editChanges.priceChange ||
    editChanges.childPriceChange
  ) {
    let url = `${import.meta.env.VITE_GCP_BASE_URL}/stripe2/updateProduct`;

    // updateProduct creates new prices when asked to change one, or when it has
    // no product to update. Checkout only uses these ids for auto-renewing
    // packages, so only those must not be saved without them.
    const requirePrices = (changes, productId) =>
      data.offerAutorenewal &&
      (changes.priceChange || changes.childPriceChange || !productId);

    let product = await postProductUpdate(url, {
      name: data.name,
      price: data.price,
      editChanges: editChanges,
      merchantId: merchantId,
      currency: currency,
      packageId: packageId,
      groupId: groupId,
    }).catch((err) => {
      logger.error("Error ", err);
    });

    if (product?.priceId) {
      stripeProductId = product.productId || stripeProductId;
      price = {
        ...price,
        priceId: product.priceId,
        shippingPriceId: product.shippingPriceId,
        priceIdWithFee: product.priceIdWithFee,
        shippingPriceIdWithFee: product.shippingPriceIdWithFee,
        secondaryPriceIdWithFee: product.secondaryPriceIdWithFees,
        secondaryShippingPriceIdWithFee:
          product.secondaryShippingPriceIdWithFees,
      };
    } else if (requirePrices(editChanges, editChanges.productId)) {
      throw new Error(
        "Couldn't update the package's Stripe prices, so nothing was saved. Please try again.",
      );
    }

    if (
      data.includesChildPrice &&
      (editChanges.nameChange || editChanges.childPriceChange)
    ) {
      let childEditChanges = {
        ...editChanges,
        productId: editChanges.childProductId,
      };
      let childProduct = await postProductUpdate(url, {
        name: `${data.name}-children`,
        price: data.childPrice,
        editChanges: childEditChanges,
        merchantId: merchantId,
        currency: currency,
        packageId: packageId,
        groupId: groupId,
        isSubProduct: true,
      }).catch((err) => {
        logger.error("Error ", err);
      });

      if (childProduct?.priceId) {
        childPrice = {
          ...childPrice,
          priceId: childProduct.priceId,
          shippingPriceId: childProduct.shippingPriceId,
          priceIdWithFee: childProduct.priceIdWithFee,
          shippingPriceIdWithFee: childProduct.shippingPriceIdWithFee,
          childProductId: childProduct.productId || childPrice.childProductId,
        };
      } else if (
        requirePrices(childEditChanges, childEditChanges.childProductId)
      ) {
        throw new Error(
          "Couldn't update the package's Stripe child prices, so nothing was saved. Please try again.",
        );
      }
    }
  }

  let packageData = { ...data, price, childPrice };
  if (stripeProductId) {
    packageData.stripeProductId = stripeProductId;
  }
  // The form copies the price into this season's timeline entry before Stripe
  // answers, so it has to be given the new ids too.
  let seasonEntry = data.packageTimeline?.[data.season];
  if (seasonEntry) {
    packageData.packageTimeline = {
      ...data.packageTimeline,
      [data.season]: { ...seasonEntry, price, childPrice },
    };
  }

  await setDoc(docRef, packageData, {
    merge: true,
  });

  let docSnap = await getDoc(doc(db, "group", groupId));
  if (docSnap.exists()) {
    let packageIdExpiryMap = docSnap.data().packageIdExpiryMap || {};
    let packageExpiryMap = docSnap.data().packageExpiryMap || {};
    packageIdExpiryMap[packageId] = expiryDate;
    packageExpiryMap[packageId] = {
      expiryDate: expiryDate || 0,
      name: data.name,
    };
    await updateDoc(doc(db, "group", groupId), {
      packageIdExpiryMap,
      packageExpiryMap,
    });
  }
}

export function getPackage(groupId, packageId) {
  return new Promise(async (resolve, reject) => {
    let data = {};
    await getDoc(
      doc(db, "registration_form_layout", groupId, "packages", packageId),
    )
      .then((doc) => {
        if (doc.exists()) {
          data = doc.data() || {};
        }
      })
      .catch(reject);

    if (!data.packageExpiryDate) {
      await getDoc(doc(db, "registration_form_layout", groupId))
        .then((doc) => {
          if (doc.exists()) {
            data.packageExpiryDate = doc.data().renewalDate;
          }
        })
        .catch(reject);
    }

    resolve(data);
  });
}

export function setPackageSortOrder(idOrderMap, groupId) {
  return new Promise((resolve, reject) => {
    let collectionRef = collection(
      db,
      "registration_form_layout",
      groupId,
      "packages",
    );

    let writePromises = Object.entries(idOrderMap).map((entry) =>
      updateDoc(doc(db, collectionRef.path, entry[0]), { sortOrder: entry[1] }),
    );
    Promise.all(writePromises)
      .then(() => resolve())
      .catch(reject);
  });
}

export function getRegsitrationLayout(groupId) {
  return new Promise((resolve, reject) => {
    getDoc(doc(db, "registration_form_layout", groupId))
      .then((doc) => {
        if (doc.exists()) {
          let data = doc.data();
          resolve(data);
          return;
        }
        resolve(null);
        return;
      })
      .catch(reject);
  });
}

// Barcelona-only ("Penya") loyalty numbering — clubId 529. Assigns the next
// groupMemberNumber to a member the moment they become active, atomically via
// group/{groupId}.nextGroupMemberNumber so concurrent activations never collide.
// A member who already has a number (including one who lost it via the manual
// SyncPenyaNumbers tool and later reactivates) is treated identically — reactivation
// always gets the next counter value, never their old number.
// Works for registered members (userGroup doc) and, via `email`, for members who
// only exist in invite_emails so far.
export async function assignGroupMemberNumberIfNeeded(
  groupId,
  memberId,
  email,
) {
  const groupRef = doc(db, "group", groupId);

  // Target the userGroup doc when it exists; otherwise fall back to the
  // invite_emails doc for the same email (member with a package who has not
  // registered on Chant yet).
  let targetRef = null;
  if (memberId) {
    const memberRef = doc(
      db,
      "userGroup",
      groupId,
      "members",
      String(memberId),
    );
    if ((await getDoc(memberRef)).exists()) targetRef = memberRef;
  }
  if (!targetRef && email) {
    const snap = await getDocs(
      query(
        collection(db, "invite_emails", groupId, "userDetails"),
        where("email", "==", email),
      ),
    );
    targetRef = snap.docs[0]?.ref || null;
  }
  if (!targetRef) return;

  await runTransaction(db, async (transaction) => {
    const groupSnap = await transaction.get(groupRef);
    if (groupSnap.data()?.clubId !== "529") return;

    const targetSnap = await transaction.get(targetRef);
    if (!targetSnap.exists() || targetSnap.data().groupMemberNumber) return;

    const nextNumber = groupSnap.data()?.nextGroupMemberNumber || 1;

    transaction.update(targetRef, { groupMemberNumber: String(nextNumber) });
    transaction.update(groupRef, { nextGroupMemberNumber: nextNumber + 1 });
  });
}
